<template>
  <div>
    <!-- ================= STEP 1: UPLOAD ================= -->
    <div v-if="step === 1">
      <p class="text-muted">
        Bring in a student's existing dues from another system's Excel export. Each row becomes real fee
        transactions against whatever fee structure that student is <strong>already mapped to</strong> (via
        the Student Category Mapping page) - there's nothing to choose here, since every student already
        belongs to one. A student not yet mapped, or a fee-head column with no matching component on their
        structure, is skipped and reported, not auto-fixed.
      </p>

      <b-form-file v-model="uploadedFile" accept=".xlsx,.xls" size="sm" @change="onFileChange"
        placeholder="Choose an Excel file or drop it here..." drop-placeholder="Drop file here..." />

      <div v-if="rawRows.length" class="mt-3">
        <p class="mb-1"><strong>How many rows (from the top) are column headings?</strong></p>
        <small class="text-muted d-block mb-1">
          Sheets like this often split the heading across two rows - a group label (e.g. "FEE HEAD") on top
          and the actual column name (e.g. "BUS", "CD") below it. Set this to 2 in that case; the columns
          highlighted below are what will be treated as headings.
        </small>
        <b-form-input type="number" min="1" :max="rawRows.length" v-model.number="headerRowCount"
          style="max-width: 120px;" />

        <div class="table-responsive mt-3">
          <table class="table table-sm table-bordered">
            <tbody>
              <tr v-for="(row, idx) in rawRows.slice(0, 6)" :key="idx"
                :class="{ 'table-warning': idx + 1 <= headerRowCount }">
                <td class="text-muted">{{ idx + 1 }}</td>
                <td v-for="(cell, cIdx) in row" :key="cIdx">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="text-right mt-3">
        <b-button variant="primary" :disabled="!rawRows.length" @click="buildColumnMappings">Next</b-button>
      </div>

      <div v-if="recentBatches.length" class="mt-4">
        <h6>Recent Imports</h6>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>When</th>
                <th>File</th>
                <th>Imported / Skipped</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="batch in recentBatches" :key="batch.id">
                <td>{{ formatDate(batch.created_at) }}</td>
                <td>{{ batch.file_name || '-' }}</td>
                <td>{{ batch.imported_rows }} / {{ batch.skipped_rows }}</td>
                <td>
                  <span v-if="batch.reverted_at" class="text-muted">Reverted</span>
                  <span v-else class="text-success">Active</span>
                </td>
                <td class="text-right">
                  <b-button v-if="!batch.reverted_at" variant="outline-danger" size="sm"
                    :disabled="revertingBatchId === batch.id" @click="revertBatch(batch)">
                    <b-spinner small v-if="revertingBatchId === batch.id" /> Revert
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ================= STEP 2: MAP COLUMNS ================= -->
    <div v-else-if="step === 2">
      <p class="text-muted">
        Only the columns we actually need are listed below - Student Registration Code, every fee-head amount
        (the components under FEE HEAD), and Start/Due/End Date. Everything else in your sheet (name, mobile,
        division, roll no., the balance total, etc.) is ignored automatically.
      </p>
      <p class="text-muted">
        Each fee-head column is matched by name against whatever fee structure that row's student is already
        on - nothing gets created automatically, so a column that doesn't match an existing component there
        will show as an error in the preview.
      </p>

      <div v-if="mappingError" class="alert alert-danger">{{ mappingError }}</div>

      <div class="table-responsive">
        <table class="table table-sm table-bordered align-middle">
          <thead class="thead-light">
            <tr>
              <th>Column in your sheet</th>
              <th style="width: 220px;">What is this?</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="mapping in columnMappings" :key="mapping.index">
              <td>{{ mapping.header }}</td>
              <td>
                <b-form-select v-model="mapping.type" :options="columnTypeOptions" class="form-control-sm" />
              </td>
              <td>
                <b-icon icon="x" style="cursor: pointer;" title="Remove this column from the import"
                  @click="removeColumnMapping(mapping)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="excludedColumns.length" class="mt-2">
        <small class="text-muted mr-2">Not included (click to add back if one of these is actually needed):</small>
        <b-badge v-for="c in excludedColumns" :key="c.index" variant="light" class="mr-1" style="cursor: pointer;"
          @click="restoreColumn(c)">
          + {{ c.header }}
        </b-badge>
      </div>

      <div class="text-right mt-3">
        <b-button variant="outline-secondary" class="mr-2" @click="step = 1">Back</b-button>
        <b-button variant="primary" :disabled="loading" @click="runDryRun">
          <b-spinner small v-if="loading" /> Preview Import
        </b-button>
      </div>
    </div>

    <!-- ================= STEP 3: PREVIEW & CONFIRM ================= -->
    <div v-else-if="step === 3">
      <div v-if="previewSummary" class="mb-3">
        <span class="badge badge-success mr-2">{{ previewSummary.ok }} ready</span>
        <span class="badge badge-warning mr-2">{{ previewSummary.warning }} with warnings</span>
        <span class="badge badge-danger mr-2">{{ previewSummary.error }} will be skipped</span>
        <span class="text-muted">of {{ previewSummary.total }} row(s)</span>
      </div>

      <div v-if="importResult" class="alert alert-success">
        Import complete - {{ importResult.summary.imported }} row(s) imported,
        {{ importResult.summary.skipped }} skipped.
      </div>

      <div class="table-responsive">
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th>Row</th>
              <th>Registration Code</th>
              <th>Student</th>
              <th>Fee Structure</th>
              <th>Status</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in previewRows" :key="row.row_index">
              <td>{{ row.row_index + 1 }}</td>
              <td>{{ row.registration_code }}</td>
              <td>{{ row.student ? row.student.name : '-' }}</td>
              <td>{{ row.category_name || '-' }}</td>
              <td>
                <span :class="statusBadgeClass(row.status)">{{ row.status }}</span>
              </td>
              <td>
                <div v-for="(msg, i) in row.errors" :key="'e' + i" class="text-danger small">{{ msg }}</div>
                <div v-for="(msg, i) in row.warnings" :key="'w' + i" class="text-warning small">{{ msg }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-right mt-3">
        <template v-if="!importResult">
          <b-button variant="outline-secondary" class="mr-2" @click="step = 2">Back</b-button>
          <b-button variant="success" :disabled="loading || !previewSummary || previewSummary.ok + previewSummary.warning === 0"
            @click="confirmImport">
            <b-spinner small v-if="loading" /> Import {{ previewSummary ? previewSummary.ok + previewSummary.warning : '' }} Row(s)
          </b-button>
        </template>
        <b-button v-else variant="primary" @click="$emit('done')">Done</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import readXlsxFile from "read-excel-file";
import moment from "moment";

const DATE_FORMATS = ["DD/MM/YYYY", "DD-MM-YYYY", "YYYY-MM-DD", "D/M/YYYY", "D-M-YYYY"];

export default {
  name: "FeeImport",
  data() {
    return {
      step: 1,
      loading: false,

      uploadedFile: null,
      fileName: null,
      rawRows: [],
      headerRowCount: 1,

      columnMappings: [],
      excludedColumns: [],
      mappingError: null,

      previewReport: null,
      importResult: null,

      recentBatches: [],
      revertingBatchId: null,

      columnTypeOptions: [
        { value: "ignore", text: "Ignore (not imported)" },
        { value: "registration_code", text: "Student Registration Code" },
        { value: "start_date", text: "Start Date" },
        { value: "due_date", text: "Due Date" },
        { value: "end_date", text: "End Date" },
        { value: "fee_head", text: "Fee Head (charge) - e.g. Opening Balance, BUS, CD, TF..." },
      ],
    };
  },
  computed: {
    previewSummary() {
      return this.previewReport ? this.previewReport.summary : null;
    },
    previewRows() {
      return this.previewReport ? this.previewReport.rows : [];
    },
  },
  async created() {
    await this.loadRecentBatches();
  },
  methods: {
    // Mess bill imports have their own screen (MessBillImport.vue).
    async loadRecentBatches() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feeimports?type=legacy`;
      const response = await this.$axios.get(url);
      this.recentBatches = response.data;
    },

    formatDate(value) {
      return value ? moment(value).format("DD-MM-YYYY HH:mm") : "-";
    },

    async revertBatch(batch) {
      if (!window.confirm(`Revert the import "${batch.file_name || batch.id}"? Students who haven't paid anything from it will have those dues removed.`)) {
        return;
      }

      this.revertingBatchId = batch.id;
      try {
        const url = this.$store.getters.getAPIKey.mainAPI + `/feeimports/${batch.id}/revert`;
        const response = await this.$axios.post(url);
        this.$toast.success(
          `Reverted ${response.data.reverted_count} student(s).` +
          (response.data.skipped_count ? ` ${response.data.skipped_count} skipped - already paid.` : "")
        );
        await this.loadRecentBatches();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "Could not revert this import.");
      } finally {
        this.revertingBatchId = null;
      }
    },

    async onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.fileName = file.name;
      try {
        this.rawRows = await readXlsxFile(file);
        this.headerRowCount = this.guessHeaderRowCount();
      } catch (e) {
        this.$toast.error("Could not read that file - " + e.message);
        this.rawRows = [];
      }
    },

    isBlankCell(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    /**
     * Sheets that split the heading across two rows (a group label like "FEE
     * HEAD" merged across several columns, with the real column names below
     * it) leave those spanned cells blank in row 1 - only the group's anchor
     * cell has text. Several such gaps is a strong signal there are 2 header
     * rows, not 1.
     */
    guessHeaderRowCount() {
      if (this.rawRows.length < 2) return 1;
      const [row1, row2] = this.rawRows;
      const width = Math.max(row1.length, row2.length);
      let groupLikeGaps = 0;

      for (let i = 0; i < width; i++) {
        if (this.isBlankCell(row1[i]) && !this.isBlankCell(row2[i])) {
          groupLikeGaps++;
        }
      }

      return groupLikeGaps >= 2 ? 2 : 1;
    },

    /**
     * Flattens N header rows into one heading per column: the bottom-most
     * non-blank cell in that column wins. This handles both shapes at once -
     * a column whose heading only exists on row 1 (e.g. "Mobile No", merged
     * vertically across the header rows) falls through to row 1, while a
     * column under a group label (e.g. "BUS" under "FEE HEAD") picks up its
     * own, more specific row-2 label instead of the group's.
     */
    flattenHeaderColumn(headerRows, col) {
      for (let r = headerRows.length - 1; r >= 0; r--) {
        const cell = headerRows[r][col];
        if (!this.isBlankCell(cell)) {
          return String(cell).trim();
        }
      }
      return null;
    },

    /**
     * Every non-blank cell in the column, top to bottom, joined together -
     * used only to guess the column's meaning (e.g. so "Balance Amount to be
     * paid" / "Balance" split across two rows still matches on "paid"), never
     * shown to the admin or used as a component name - flattenHeaderColumn()
     * handles that.
     */
    fullHeaderTextColumn(headerRows, col) {
      return headerRows
        .map((row) => row[col])
        .filter((cell) => !this.isBlankCell(cell))
        .map((cell) => String(cell).trim())
        .join(" ");
    },

    buildColumnMappings() {
      const headerRows = this.rawRows.slice(0, this.headerRowCount);
      const dataRows = this.rawRows.slice(this.headerRowCount);
      const width = Math.max(...headerRows.map((row) => row.length));

      // Drop fully-empty rows (trailing blank lines Excel often leaves behind).
      this.dataRows = dataRows.filter((row) => row.some((cell) => !this.isBlankCell(cell)));

      this.columnMappings = [];
      this.excludedColumns = [];

      for (let index = 0; index < width; index++) {
        const header = this.flattenHeaderColumn(headerRows, index) || `Column ${index + 1}`;
        const fullText = this.fullHeaderTextColumn(headerRows, index) || header;
        const type = this.guessColumnType(fullText);
        const mapping = { index, header, type };

        // Only the columns we actually need (registration code, fee heads,
        // and the 3 dates) are shown for mapping - everything else (name,
        // mobile, division, roll no., the balance total, ...) is left out
        // entirely rather than listed with "Ignore" pre-selected.
        if (type === "ignore") {
          this.excludedColumns.push(mapping);
        } else {
          this.columnMappings.push(mapping);
        }
      }

      this.step = 2;
    },

    removeColumnMapping(mapping) {
      this.columnMappings = this.columnMappings.filter((m) => m.index !== mapping.index);
      this.excludedColumns.push(mapping);
    },

    restoreColumn(mapping) {
      this.excludedColumns = this.excludedColumns.filter((m) => m.index !== mapping.index);
      mapping.type = "fee_head";
      this.columnMappings.push(mapping);
    },

    // Guesses run against the full (unflattened) header text for this
    // column so a group label split across rows (e.g. "Balance Amount to be
    // paid" over "Balance") is still recognised - see fullHeaderTextColumn().
    guessColumnType(fullText) {
      const h = fullText.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
      if (/registration.*(no|code)|admission.*(no|code)/.test(h)) return "registration_code";
      if (/start.*date/.test(h)) return "start_date";
      if (/due.*date/.test(h)) return "due_date";
      if (/end.*date/.test(h)) return "end_date";
      // Everything not explicitly needed - including the sheet's own
      // "Balance Amount to be paid" total - is left out by default; only
      // registration code, dates, and FEE HEAD components are wanted.
      if (
        /^(si\s*no|sl\s*no|division|roll\s*no|first\s*name|last\s*name|name|mobile(\s*no)?|standard\s*course|class|section)$/.test(h) ||
        /balance.*(paid|due|amount)|^balance$/.test(h)
      ) {
        return "ignore";
      }
      return "fee_head";
    },

    parseAmount(value) {
      if (value === null || value === undefined || value === "" || value === "-") return 0;
      if (typeof value === "number") return value;
      const cleaned = String(value).replace(/,/g, "").trim();
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    },

    parseDate(value) {
      if (value === null || value === undefined || value === "") return null;
      if (value instanceof Date) return moment(value).format("YYYY-MM-DD");
      const parsed = moment(String(value).trim(), DATE_FORMATS, true);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
    },

    validateMappings() {
      const counts = {};
      this.columnMappings.forEach((m) => {
        counts[m.type] = (counts[m.type] || 0) + 1;
      });

      if ((counts.registration_code || 0) !== 1) {
        return "Map exactly one column as \"Student Registration Code\".";
      }
      ["start_date", "due_date", "end_date"].forEach((type) => {
        if ((counts[type] || 0) > 1) {
          return `Only one column can be mapped as "${type}".`;
        }
      });

      return null;
    },

    columnIndexOf(type) {
      const mapping = this.columnMappings.find((m) => m.type === type);
      return mapping ? mapping.index : null;
    },

    buildRequestRows() {
      const codeIdx = this.columnIndexOf("registration_code");
      const startIdx = this.columnIndexOf("start_date");
      const dueIdx = this.columnIndexOf("due_date");
      const endIdx = this.columnIndexOf("end_date");
      // Every fee-head column - Opening Balance, BUS, CD, TF, ... - is just
      // another charge, matched by name against whatever fee structure that
      // row's student turns out to already be on, and sharing this row's
      // Start/Due/End dates like everything else in the FEE HEAD group.
      const feeHeadMappings = this.columnMappings.filter((m) => m.type === "fee_head");

      return this.dataRows.map((row) => ({
        registration_code: (row[codeIdx] || "").toString().trim(),
        start_date: startIdx !== null ? this.parseDate(row[startIdx]) : null,
        due_date: dueIdx !== null ? this.parseDate(row[dueIdx]) : null,
        end_date: endIdx !== null ? this.parseDate(row[endIdx]) : null,
        fee_heads: feeHeadMappings.map((m) => ({
          column_name: m.header,
          amount: this.parseAmount(row[m.index]),
        })),
      }));
    },

    async runDryRun() {
      this.mappingError = this.validateMappings();
      if (this.mappingError) return;

      this.loading = true;
      try {
        const url = this.$store.getters.getAPIKey.mainAPI + `/feeimports`;
        const response = await this.$axios.post(url, {
          dry_run: true,
          rows: this.buildRequestRows(),
        });
        this.previewReport = response.data;
        this.importResult = null;
        this.step = 3;
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "Could not preview this import.");
      } finally {
        this.loading = false;
      }
    },

    async confirmImport() {
      this.loading = true;
      try {
        const url = this.$store.getters.getAPIKey.mainAPI + `/feeimports`;
        const response = await this.$axios.post(url, {
          dry_run: false,
          file_name: this.fileName,
          column_mapping: this.columnMappings,
          rows: this.buildRequestRows(),
        });
        this.importResult = response.data;
        this.previewReport = response.data;
        this.$toast.success("Import complete");
        await this.loadRecentBatches();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "Import failed.");
      } finally {
        this.loading = false;
      }
    },

    statusBadgeClass(status) {
      return {
        badge: true,
        "badge-success": status === "ok",
        "badge-warning": status === "warning",
        "badge-danger": status === "error",
      };
    },
  },
};
</script>
